import { mapValues } from "lodash-es";
import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import { i18n } from "@/i18n";
import { showConfirm } from "@core/ui/alert/alert";

import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useConditionStore } from "@editor/items/condition/conditions.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useStakeholderStore } from "../stakeholder/stakeholders.store";
import { useFlowBranchStore } from "../flow/flowBranches.store";
import { useMomentumStore } from "../momentum/momentums.store";
import { useSceneNodesStore } from "../scene/sceneNodes.store";

import { mapNumber } from "@/helpers/utility/MathHelper";

import { changeElementType } from "../element/elementFunctions";

import type { ResourceItem } from "./ResourceItem";
import type { IndexItem } from "../index/IndexItem";
import type { TargetItem } from "../target/TargetItem";
import type { ExpressionValue } from "../expression/expressionFunctions";

export const resourceItemStructure: ResourceItem = {
  type: "resource",
  docs: {},
  id: 0,
  uid: "resource_0",

  title: "",
  description: "",

  value: 0,
  unit: "",
  minValue: 0,
  maxValue: 1_000,

  // controllable: false,

  createdAt: 0
};

export const useResourceStore = defineItemStore(
  "model/resources",
  resourceItemStructure,
  {
    getters: {
      /**
       * The titles of every resource, by id
       */
      titles(state): { [resourceId: number]: string } {
        return mapValues(state.items, (r) => r.title);
      },
      /**
       * The values of every resource, by id
       */
      values(state): { [resourceId: number]: number } {
        return mapValues(state.items, (r) => r.value);
      },

      expressionValues(state): { [resourceUid: string]: ExpressionValue } {
        const values: { [resourceUid: string]: ExpressionValue } = {};
        for (const resource of Object.values(state.items)) {
          values[resource.uid] = {
            value: resource.value,
            minValue: resource.minValue,
            maxValue: resource.maxValue,
            unit: resource.unit,
            unitValid: true,
            valid: true
          };
        }
        return values;
      }
    },
    events: {
      afterUpdate(resource, updatedData, resourceBefore) {
        // make sure the relation graphs do not leave the bounds
        if (
          ("minValue" in updatedData || "maxValue" in updatedData) &&
          (resource.maxValue != null && resource.minValue != null)
        ) {
          const relations = useRelationStore().byStartUid[resource.uid] ?? [];
          const oldMinValue = resourceBefore?.minValue;
          const oldMaxValue = resourceBefore?.maxValue;

          if (oldMinValue != null && oldMaxValue != null) {
            for (const relation of relations) {
              for (const point of relation.curvePoints) {
                point.x = mapNumber(
                  point.x,
                  oldMinValue,
                  oldMaxValue,
                  resource.minValue,
                  resource.maxValue
                );
              }
            }
          }
        }
      },
      async canBeDeleted(resource) {
        const inSimulations = useSimulationStore().orderedItems.some((simulation) => {
          const elementIds = Object.keys(simulation.startElementValues);
          return elementIds.includes(resource.uid);
        });
        if (!inSimulations) return true;
        return await showConfirm(
          i18n.t("questions.deleteResource"),
          i18n.t("actions.deleteResource"),
          i18n.t("labels.abort")
        );
      },

      afterDelete(resource) {
        const uid = resource.uid;
        const promises = [];

        // delete the owned MOMENTUM
        const momentumStore = useMomentumStore();
        const momentum = momentumStore.byElementUid[uid];
        if (momentum != null) {
          promises.push(
            momentumStore.delete({ id: momentum.id, instant: true })
          );
        }

        const relationStore = useRelationStore();
        const allConnectedRelations = relationStore.orderedItems.filter(
          (relation) =>
            (relation.startItemType === "resource" &&
              relation.startItemId === resource.id) ||
            (relation.endItemId === resource.id &&
              relation.endItemType === "resource")
        );
        if (allConnectedRelations.length > 0) {
          promises.push(relationStore.deleteMany(allConnectedRelations));
        }

        const flowBranchStore = useFlowBranchStore();
        const allConnectedFlowBranches = flowBranchStore.orderedItems.filter(
          (flowBranch) =>
            flowBranch.elementId === resource.id &&
            flowBranch.elementType === "resource"
        );
        if (allConnectedFlowBranches.length > 0) {
          promises.push(flowBranchStore.deleteMany(allConnectedFlowBranches));
        }

        // delete effects linked to resource
        const effectStore = useEffectStore();
        const effects = effectStore.byResource[resource.id];
        if (effects) promises.push(effectStore.deleteMany(effects));

        // delete conditions
        const conditionStore = useConditionStore();
        const conditions = conditionStore.getByActivator(resource);
        if (conditions) {
          promises.push(conditionStore.deleteMany(Object.values(conditions)));
        }

        // get all actions that are using the resource as cost
        const actionStore = useActionStore();
        const actions = actionStore.orderedItems.filter((action) => action.costs.some((cost) => cost.elementUid == resource.uid));
        for (const action of actions) {
          promises.push(
            actionStore.deleteCost({
              id: action.id,
              elementUid: resource.uid
            })
          );
        }

        // remove resource starting values
        const scenarioStore = useScenarioStore();
        const scenarios = scenarioStore.getChangingResource(resource.id);
        if (scenarios) {
          for (const scenario of scenarios) {
            promises.push(
              scenarioStore.deleteResourceValue({
                id: scenario.id,
                resourceId: resource.id
              })
            );
          }
        }

        // remove start values for this element from all SIMULATIONS
        const simulationStore = useSimulationStore();
        const simulations = simulationStore.items || {};
        for (const simulationId in simulations) {
          const simulation = simulations[simulationId];
          if (simulation.startElementValues[uid] != null) {
            promises.push(
              simulationStore.deleteResourceValue({
                id: simulation.id,
                resourceId: resource.id
              })
            );
          }
        }

        const stakeholderStore = useStakeholderStore();
        const stakeholders = stakeholderStore.byResource[resource.id] ?? [];

        for (const stakeholder of stakeholders) {
          stakeholderStore.removeResource({
            id: stakeholder.id,
            resourceId: resource.id
          });
        }

        const sceneNodeStore = useSceneNodesStore();
        const nodes = sceneNodeStore.byNodeType.element ?? [];
        const resourceNodes = nodes.filter((node) => node.data.elementUid === uid);
        for (const node of resourceNodes) {
          promises.push(
            sceneNodeStore.delete({ id: node.id, instant: true })
          );
        }

        return Promise.all(promises);
      }
    },
    actions: {
      async changeType(
        id: number,
        type: "index" | "target"
      ): Promise<IndexItem | TargetItem | undefined> {
        const resource = this.items[id];
        if (resource == null) return;

        const newElement = await changeElementType(resource, type);

        await this.delete({ id: resource.id, instant: true });
        return newElement;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHotItemStore(useResourceStore, import.meta.hot));
}
